<template>
  <section class="AiCarouselWithSections Layout">
    <article class="Layout-section">
      <grid-item
        :sm="mobileSize"
        md="11"
        offset-md="1"
        class="AiCarouselWithSections-gridTitle">
        <ai-typo
          class="AiCarouselWithSections-title"
          as="h2"
          variant="heading-02">
          {{ title }}
        </ai-typo>
      </grid-item>

      <grid-item sm="4" :md="horizontalTabs ? 10 : 2" offset-md="1">
        <ai-tabs
          v-if="sections.length"
          v-model="currentSection"
          :tabs-variant="tabsVariant"
          :items="sections"
          :size="tabsSize"
          :anchors="true" />
      </grid-item>

      <grid-item
        sm="4"
        :md="horizontalTabs ? 10 : 8"
        :offset-md="horizontalTabs ? 1 : 0">
        <ai-carousel
          v-if="items && items.length"
          :ref="accessId"
          v-model="currentStep"
          :access-id="accessId"
          :items="items"
          :mobile-button="mobileButton"
          :gutter="gutter"
          :hide-progress-bar="hideProgressBar"
          :slide-width="slideWidth"
          :arrows-top-position="arrowsPosition"
          :hide-gallery="hideGallery"
          @open-media-gallery="galleryOpened = true" />
      </grid-item>

      <ai-media-gallery
        v-if="galleryItems && !hideGallery"
        v-model="galleryOpened"
        v-model:step="currentStep"
        :items="galleryItems" />
    </article>
  </section>
</template>

<script setup lang="ts">
import AiTypo from '../../atoms/AiTypo/AiTypo.vue';
import AiCarousel from '../../molecules/AiCarousel/AiCarousel.vue';
import type { AiCarouselItem } from '../../molecules/AiCarousel/types';
import AiMediaGallery from '../../molecules/AiMediaGallery/AiMediaGallery.vue';
import AiTabs from '../../molecules/AiTabs/AiTabs.vue';

export interface AiCarouselItemWithSection extends AiCarouselItem {
  sectionId: string;
}

type CarouselSection = {
  label: string;
  id: string;
};

interface AiCarouselWithSectionsProps {
  accessId: string;
  title: string;
  hideProgressBar?: boolean;
  sections: CarouselSection[];
  items: AiCarouselItemWithSection[];
  gutter?: number;
  slideWidth?: number;
  hideGallery?: boolean;
  mobileButton?: boolean;
  galleryItems?: AiCarouselItem[];
  arrowsPosition?: string;
  horizontalTabs?: boolean;
  mobileSize: string;
  tabsVariant: 'tab-link-01' | 'tab-link-02';
  tabsSize?: 'large' | 'medium' | 'small';
}

const props = defineProps<AiCarouselWithSectionsProps>();

const sections = computed(() => {
  if (!Array.isArray(props.sections)) return [];

  return props.sections.map(section => ({
    label: section.label,
    value: section.id,
  }));
});

const currentSection = ref<string>(sections?.value?.[0]?.value ?? undefined);

const currentStep = ref<number>(1);
const galleryOpened = ref<boolean>(false);

watch(currentSection, sectionId => {
  if (props.items.length <= 0) return;

  const currentItem = props.items[currentStep.value - 1];
  const index = props.items.findIndex(item => item.sectionId === sectionId);

  if (currentItem.sectionId !== sectionId) {
    currentStep.value = index + 1;
  }
});

watch(currentStep, step => {
  if (props.items.length <= 0) return;

  const item = props.items[step - 1];

  currentSection.value = item.sectionId;
});
</script>

<style lang="scss" scoped>
@use '@/assets/styles/utilities/constants';

.AiCarouselWithSections {
  padding-top: constants.$padding-07;
  padding-bottom: constants.$padding-07;
}

.AiCarouselWithSections-title {
  margin-bottom: constants.$margin-04;
}

.AiCarouselWithSections-gridTitle {
  width: 100%;
}
</style>
